import { decodeProject, encodeProject } from "./rle.js";

export const TRACK_COUNT = 16;
export const TRACK_STRUCT_SIZE = 17876;
export const CLONE_STRUCT_SIZE = TRACK_STRUCT_SIZE - 1;
export const STEP_TICKS = 480;
export const NOTE_SIZE = 12;

const GLOBAL_SCENE_COUNT = 0x06;
const GLOBAL_SELECTED_SONG = 0x07;
const GLOBAL_MIDI = 0x55;
const SCENE_SLOT0 = 0x95;
const SCENE_SLOT_SIZE = 33;
const FOOTER_SLOTS = 14;

const OFF_PATTERN_STEPS = 0x01;
const OFF_PRISTINE = 0x11;
const OFF_ENGINE = 0x14;
const OFF_NOTE_COUNT = 0x456f;
const OFF_STEPCOMP = 0x3057;
const OFF_PLOCK = 0x2a0;
const OFF_PLOCK_STEP_FLAG = 0x2c4e;
const OFF_PLOCK_MASTER = 0x304e;
const OFF_PRESET_PATH = 0x453f;
const MIN_PATTERN_BYTES = OFF_NOTE_COUNT + 1;

export const STEP_COMPONENTS = [
  "pulse",
  "hold",
  "multiply",
  "velocity",
  "ramp up",
  "ramp down",
  "random",
  "portamento",
  "bend",
  "tonality",
  "jump",
  "param",
  "conditional A",
  "conditional B",
];

export const PLOCK_PARAMS = [
  ["volume", 0],
  ["param 1", 2],
  ["param 2", 4],
  ["param 3", 6],
  ["param 4", 8],
  ["amp attack", 18],
  ["amp decay", 20],
  ["amp sustain", 22],
  ["amp release", 24],
  ["poly", 26],
  ["portamento", 28],
  ["pitch bend", 30],
  ["engine volume", 32],
  ["cutoff", 34],
  ["resonance", 36],
  ["filter env amount", 38],
  ["key tracking", 40],
  ["send ext", 42],
  ["send tape", 44],
  ["send FX I", 46],
  ["send FX II", 48],
  ["lfo param", 50],
  ["lfo dest", 52],
  ["filter env attack", 66],
  ["filter env decay", 68],
  ["filter env sustain", 70],
  ["filter env release", 72],
  ["pan", 82],
];

function readU16(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function readU32(bytes, offset) {
  return (
    bytes[offset] |
    (bytes[offset + 1] << 8) |
    (bytes[offset + 2] << 16) |
    (bytes[offset + 3] << 24)
  ) >>> 0;
}

function readCString(bytes, offset, maxLength = 96) {
  const chars = [];
  for (let i = 0; i < maxLength && offset + i < bytes.length; i += 1) {
    const byte = bytes[offset + i];
    if (byte === 0) break;
    if (byte >= 32 && byte <= 126) chars.push(String.fromCharCode(byte));
  }
  return chars.join("");
}

function writeCString(bytes, offset, value, maxLength = 96) {
  const text = String(value ?? "");
  if (!/^[\x20-\x7e]*$/.test(text)) {
    throw new Error("OP-XY project paths must use printable ASCII characters.");
  }
  if (text.length >= maxLength) {
    throw new Error(`OP-XY project path is too long. Maximum is ${maxLength - 1} characters.`);
  }

  for (let i = 0; i < maxLength && offset + i < bytes.length; i += 1) {
    bytes[offset + i] = 0;
  }
  for (let i = 0; i < text.length && offset + i < bytes.length; i += 1) {
    bytes[offset + i] = text.charCodeAt(i);
  }
}

function writeU32(bytes, offset, value) {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >> 8) & 0xff;
  bytes[offset + 2] = (value >> 16) & 0xff;
  bytes[offset + 3] = (value >> 24) & 0xff;
}

function writeU16(bytes, offset, value) {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >> 8) & 0xff;
}

function clampInt(value, min, max) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.max(min, Math.min(max, Math.trunc(parsed)));
}

function patternOffset(pattern, leaderOffset) {
  return pattern.base + leaderOffset;
}

function noteSort(a, b) {
  return a.tick - b.tick || a.note - b.note || a.velocity - b.velocity;
}

export class XyProject {
  static fromBytes(bytes, name = "project.xy") {
    const decoded = decodeProject(bytes);
    return new XyProject(decoded.header, decoded.image, name);
  }

  constructor(header, image, name) {
    this.header = header;
    this.image = new Uint8Array(image);
    this.name = name;
    this.patternStarts = this.findPatternStarts();
    this.tracks = this.parseTracks();
    this.scenes = this.parseScenes();
    this.songs = this.parseSongs();
  }

  get tempo() {
    return readU16(this.image, 0) / 10;
  }

  get selectedSongIndex() {
    return this.image[GLOBAL_SELECTED_SONG];
  }

  encode() {
    return encodeProject(this.header, this.image);
  }

  refresh() {
    this.patternStarts = this.findPatternStarts();
    this.tracks = this.parseTracks();
    this.scenes = this.parseScenes();
    this.songs = this.parseSongs();
  }

  findPatternStarts() {
    const starts = [];
    for (let base = 0; base <= this.image.length - 11; base += 1) {
      if (this.looksLikePatternStart(base)) {
        starts.push(base);
      }
    }
    return starts;
  }

  looksLikePatternStart(base) {
    if (base + MIN_PATTERN_BYTES > this.image.length) return false;
    if (this.image[base] > 9) return false;
    if (this.image[base + 1] < 1 || this.image[base + 1] > 64) return false;
    if (
      this.image[base + 2] !== 0xf0 ||
      this.image[base + 3] !== 0x00 ||
      this.image[base + 4] !== 0x00 ||
      this.image[base + 5] !== 0x00
    ) {
      return false;
    }
    if (this.image[base + 6] > 0x1f) return false;
    if (this.image[base + 9] !== 0xfc && this.image[base + 9] !== 0x00) return false;
    if (this.image[base + 10] !== 0x00) return false;

    const noteCount = this.image[base + OFF_NOTE_COUNT] || 0;
    if (noteCount > 120) return false;
    const noteEnd = base + OFF_NOTE_COUNT + 1 + noteCount * NOTE_SIZE;
    if (noteEnd > this.image.length) return false;

    for (let noteIndex = 0; noteIndex < Math.min(noteCount, 8); noteIndex += 1) {
      const noteOffset = base + OFF_NOTE_COUNT + 1 + noteIndex * NOTE_SIZE;
      if (this.image[noteOffset + 8] > 127) return false;
      if (this.image[noteOffset + 9] > 127) return false;
    }

    return true;
  }

  parseTracks() {
    const tracks = [];
    let startIndex = 0;

    for (let trackIndex = 0; trackIndex < TRACK_COUNT; trackIndex += 1) {
      const leaderBase = this.patternStarts[startIndex];
      if (!Number.isInteger(leaderBase)) {
        throw new Error(`Could not find decoded pattern struct for track ${trackIndex + 1}.`);
      }
      const patternCount = Math.max(1, this.image[leaderBase] || 1);
      const patterns = [];

      patterns.push(this.parsePattern(trackIndex, 0, leaderBase, true));
      for (let patternIndex = 1; patternIndex < patternCount; patternIndex += 1) {
        const cloneBase = this.patternStarts[startIndex + patternIndex];
        patterns.push(this.parsePattern(trackIndex, patternIndex, cloneBase, false));
      }

      tracks.push({
        index: trackIndex,
        label: `T${trackIndex + 1}`,
        displayLabel: trackIndex < 8 ? `T${trackIndex + 1}` : `A${trackIndex - 7}`,
        group: trackIndex < 8 ? "instrument" : "aux",
        base: leaderBase,
        engineId: this.image[leaderBase + OFF_ENGINE],
        presetPath: readCString(this.image, leaderBase + OFF_PRESET_PATH),
        midiChannel: this.image[GLOBAL_MIDI + trackIndex] === 0xff
          ? null
          : this.image[GLOBAL_MIDI + trackIndex] + 1,
        patternCount,
        patterns,
      });
      startIndex += patternCount;
    }

    return tracks;
  }

  parsePattern(trackIndex, patternIndex, base, isLeader) {
    const offset = (relative) => base + relative;
    const steps = this.image[offset(OFF_PATTERN_STEPS)] || 16;
    const noteCountOffset = offset(OFF_NOTE_COUNT);
    const noteCount = this.image[noteCountOffset] || 0;
    const notes = [];

    for (let i = 0; i < noteCount; i += 1) {
      const noteOffset = noteCountOffset + 1 + i * NOTE_SIZE;
      const tick = readU32(this.image, noteOffset);
      notes.push({
        id: `${trackIndex}:${patternIndex}:${i}`,
        index: i,
        tick,
        step: Math.floor(tick / STEP_TICKS) + 1,
        tickOffset: tick % STEP_TICKS,
        gate: readU32(this.image, noteOffset + 4),
        note: this.image[noteOffset + 8],
        velocity: this.image[noteOffset + 9],
        flags: [this.image[noteOffset + 10], this.image[noteOffset + 11]],
      });
    }

    return {
      trackIndex,
      patternIndex,
      base,
      isLeader,
      steps,
      noteCount,
      notes,
      engineId: this.image[offset(OFF_ENGINE)],
      presetPath: readCString(this.image, offset(OFF_PRESET_PATH)),
      stepComponents: this.parseStepComponents({ base, isLeader }, steps),
      plocks: this.parsePlocks({ base, isLeader }, steps),
    };
  }

  parseScenes() {
    const count = Math.max(1, (this.image[GLOBAL_SCENE_COUNT] || 0) + 1);
    const scenes = [];

    for (let sceneIndex = 0; sceneIndex < count; sceneIndex += 1) {
      const offset = SCENE_SLOT0 + sceneIndex * SCENE_SLOT_SIZE;
      const selectedPatterns = Array.from(this.image.slice(offset, offset + TRACK_COUNT));
      const mutes = Array.from(this.image.slice(offset + TRACK_COUNT, offset + TRACK_COUNT * 2));
      scenes.push({
        index: sceneIndex,
        label: sceneIndex === 0 ? "Live" : `Scene ${sceneIndex}`,
        selectedPatterns,
        mutes,
        flags: this.image[offset + 32],
      });
    }

    return scenes;
  }

  parseSongs() {
    const footerStart = this.findSongFooterStart();
    const songs = [];
    let cursor = footerStart;

    for (let index = 0; index < FOOTER_SLOTS && cursor < this.image.length; index += 1) {
      const sceneCount = this.image[cursor] || 0;
      const sceneIds = Array.from(this.image.slice(cursor + 1, cursor + 1 + sceneCount));
      const loopA = this.image[cursor + 1 + sceneCount] ?? 0;
      const loopB = this.image[cursor + 2 + sceneCount] ?? 0;
      const size = Math.max(4, 1 + sceneCount + 2);
      songs.push({
        index,
        label: `Song ${index + 1}`,
        sceneCount,
        sceneIds,
        loop: loopA === 0,
        offset: cursor,
        size,
      });
      cursor += size;
    }

    return songs;
  }

  findSongFooterStart() {
    const min = Math.max(0, this.image.length - 160);
    let best = this.image.length - FOOTER_SLOTS * 4;
    let bestScore = -1;

    for (let candidate = min; candidate < this.image.length; candidate += 1) {
      let cursor = candidate;
      let score = 0;
      let valid = true;
      for (let slot = 0; slot < FOOTER_SLOTS; slot += 1) {
        const count = this.image[cursor];
        if (count > 14 || cursor + 1 + count + 2 > this.image.length) {
          valid = false;
          break;
        }
        const scenes = Array.from(this.image.slice(cursor + 1, cursor + 1 + count));
        if (scenes.some((scene) => scene > 13)) {
          valid = false;
          break;
        }
        if (slot === 0 && count > 1) score += 20 + count;
        if (count === 1 && this.image[cursor + 1] === 0) score += 1;
        if (this.image[cursor + 1 + count] <= 1 && this.image[cursor + 2 + count] <= 1) score += 1;
        cursor += Math.max(4, 1 + count + 2);
      }
      if (valid && cursor <= this.image.length && score > bestScore) {
        best = candidate;
        bestScore = score;
      }
    }

    return best;
  }

  parseStepComponents(pattern, steps) {
    const rows = [];
    for (let step = 1; step <= Math.min(64, steps); step += 1) {
      const offset = patternOffset(pattern, OFF_STEPCOMP) + (step - 1) * 16;
      const mask = readU16(this.image, offset);
      const components = [];
      for (let bit = 0; bit < STEP_COMPONENTS.length; bit += 1) {
        if (mask & (1 << bit)) {
          components.push({
            bit,
            name: STEP_COMPONENTS[bit],
            value: this.image[offset + 2 + bit],
          });
        }
      }
      rows.push({ step, mask, components });
    }
    return rows;
  }

  parsePlocks(pattern, steps) {
    const rows = [];
    const master = this.image[patternOffset(pattern, OFF_PLOCK_MASTER)] === 1;

    for (let step = 1; step <= Math.min(64, steps); step += 1) {
      const flag = this.image[patternOffset(pattern, OFF_PLOCK_STEP_FLAG) + (step - 1) * 8] === 1;
      const rowOffset = patternOffset(pattern, OFF_PLOCK) + (step - 1) * 84;
      const values = [];
      for (const [name, byteOffset] of PLOCK_PARAMS) {
        const value = readU16(this.image, rowOffset + byteOffset);
        if ((master && flag && value !== 0) || value !== 0) {
          values.push({ name, byteOffset, value });
        }
      }
      rows.push({ step, active: master && flag, values });
    }
    return rows;
  }

  getPattern(trackIndex, patternIndex) {
    return this.tracks[trackIndex]?.patterns[patternIndex] ?? null;
  }

  setTrackPresetPath(trackIndex, presetPath) {
    const track = this.tracks[trackIndex];
    if (!track) {
      throw new Error("Track does not exist.");
    }

    for (const pattern of track.patterns) {
      writeCString(this.image, pattern.base + OFF_PRESET_PATH, presetPath);
    }

    this.refresh();
  }

  replaceEmbeddedPath(oldPath, newPath) {
    const oldText = String(oldPath ?? "");
    const newText = String(newPath ?? "");
    if (!oldText || !newText) return 0;
    if (!/^[\x20-\x7e]*$/.test(oldText) || !/^[\x20-\x7e]*$/.test(newText)) {
      throw new Error("OP-XY embedded paths must use printable ASCII characters.");
    }
    if (newText.length > oldText.length) {
      throw new Error("Embedded project sample paths can only be replaced with same-length or shorter paths.");
    }

    const oldBytes = Array.from(oldText, (char) => char.charCodeAt(0));
    const newBytes = Array.from(newText, (char) => char.charCodeAt(0));
    let replacements = 0;

    for (let offset = 0; offset <= this.image.length - oldBytes.length; offset += 1) {
      let matches = true;
      for (let index = 0; index < oldBytes.length; index += 1) {
        if (this.image[offset + index] !== oldBytes[index]) {
          matches = false;
          break;
        }
      }
      if (!matches) continue;

      const previousByte = offset === 0 ? 0 : this.image[offset - 1];
      const nextByte = this.image[offset + oldBytes.length] ?? 0;
      const previousBoundary = previousByte === 0 || previousByte < 0x20 || previousByte > 0x7e;
      const nextBoundary = nextByte === 0 || nextByte < 0x20 || nextByte > 0x7e;
      if (!previousBoundary || !nextBoundary) continue;

      for (let index = 0; index < oldBytes.length; index += 1) {
        this.image[offset + index] = newBytes[index] ?? 0;
      }
      replacements += 1;
      offset += oldBytes.length - 1;
    }

    if (replacements > 0) this.refresh();
    return replacements;
  }

  setScenePattern(sceneIndex, trackIndex, patternIndex) {
    const scene = this.scenes[sceneIndex];
    const track = this.tracks[trackIndex];
    if (!scene || !track) {
      throw new Error("Scene or track does not exist.");
    }
    const nextPattern = clampInt(patternIndex, 0, track.patternCount - 1);
    const offset = SCENE_SLOT0 + sceneIndex * SCENE_SLOT_SIZE + trackIndex;
    this.image[offset] = nextPattern;
    this.scenes = this.parseScenes();
  }

  setSceneMute(sceneIndex, trackIndex, muted) {
    const scene = this.scenes[sceneIndex];
    const track = this.tracks[trackIndex];
    if (!scene || !track) {
      throw new Error("Scene or track does not exist.");
    }
    const offset = SCENE_SLOT0 + sceneIndex * SCENE_SLOT_SIZE + TRACK_COUNT + trackIndex;
    this.image[offset] = muted ? 1 : 0;
    this.scenes = this.parseScenes();
  }

  duplicatePattern(trackIndex, patternIndex) {
    const track = this.tracks[trackIndex];
    const source = this.getPattern(trackIndex, patternIndex);
    if (!track || !source) {
      throw new Error("Track or pattern does not exist.");
    }
    if (track.patternCount >= 9) {
      throw new Error("OP-XY track pattern limit is 9 patterns.");
    }

    const lastPattern = track.patterns[track.patterns.length - 1];
    const sourceSize = CLONE_STRUCT_SIZE + source.noteCount * NOTE_SIZE;
    const insertAfterHeaderOffset = lastPattern.base + CLONE_STRUCT_SIZE + lastPattern.noteCount * NOTE_SIZE + 1;
    const payload = new Uint8Array(sourceSize);

    payload.set(this.image.slice(source.base + 1, source.base + sourceSize), 0);
    payload[payload.length - 1] = 0;

    const next = new Uint8Array(this.image.length + payload.length);
    next.set(this.image.slice(0, insertAfterHeaderOffset), 0);
    next.set(payload, insertAfterHeaderOffset);
    next.set(this.image.slice(insertAfterHeaderOffset), insertAfterHeaderOffset + payload.length);
    next[track.base] = track.patternCount + 1;

    this.image = next;
    this.refresh();
    return track.patternCount;
  }

  duplicatePatternForScene(sceneIndex, trackIndex, patternIndex) {
    const newPatternIndex = this.duplicatePattern(trackIndex, patternIndex);
    this.setScenePattern(sceneIndex, trackIndex, newPatternIndex);
    return newPatternIndex;
  }

  setPlockValue(trackIndex, patternIndex, step, param, value) {
    const pattern = this.getPattern(trackIndex, patternIndex);
    if (!pattern) {
      throw new Error("Pattern does not exist.");
    }

    const paramEntry = typeof param === "number"
      ? PLOCK_PARAMS.find(([, byteOffset]) => byteOffset === param)
      : PLOCK_PARAMS.find(([name]) => name === param);
    if (!paramEntry) {
      throw new Error(`Unknown p-lock parameter: ${param}`);
    }

    const stepIndex = clampInt(step, 1, pattern.steps) - 1;
    const [, byteOffset] = paramEntry;
    const rowOffset = patternOffset(pattern, OFF_PLOCK) + stepIndex * 84;
    const nextValue = clampInt(value, 0, 0xffff);

    this.image[patternOffset(pattern, OFF_PLOCK_MASTER)] = 1;
    writeU16(this.image, rowOffset + byteOffset, nextValue);

    const flagOffset = patternOffset(pattern, OFF_PLOCK_STEP_FLAG) + stepIndex * 8;
    this.image[flagOffset] = this.stepHasAnyPlock(pattern, stepIndex) ? 1 : 0;

    this.markEdited(trackIndex, patternIndex);
    this.refresh();
  }

  clearPlockValue(trackIndex, patternIndex, step, param) {
    this.setPlockValue(trackIndex, patternIndex, step, param, 0);
  }

  stepHasAnyPlock(pattern, stepIndex, ignoringByteOffset = null) {
    const rowOffset = patternOffset(pattern, OFF_PLOCK) + stepIndex * 84;
    return PLOCK_PARAMS.some(([, byteOffset]) => {
      if (byteOffset === ignoringByteOffset) return false;
      return readU16(this.image, rowOffset + byteOffset) !== 0;
    });
  }

  setStepComponentValue(trackIndex, patternIndex, step, component, value) {
    const pattern = this.getPattern(trackIndex, patternIndex);
    if (!pattern) {
      throw new Error("Pattern does not exist.");
    }

    const bit = typeof component === "number" ? component : STEP_COMPONENTS.indexOf(component);
    if (bit < 0 || bit >= STEP_COMPONENTS.length) {
      throw new Error(`Unknown step component: ${component}`);
    }

    const stepIndex = clampInt(step, 1, pattern.steps) - 1;
    const rowOffset = patternOffset(pattern, OFF_STEPCOMP) + stepIndex * 16;
    const mask = readU16(this.image, rowOffset) | (1 << bit);
    writeU16(this.image, rowOffset, mask);
    this.image[rowOffset + 2 + bit] = clampInt(value, 0, 0xff);

    this.markEdited(trackIndex, patternIndex);
    this.refresh();
  }

  clearStepComponent(trackIndex, patternIndex, step, component) {
    const pattern = this.getPattern(trackIndex, patternIndex);
    if (!pattern) {
      throw new Error("Pattern does not exist.");
    }

    const bit = typeof component === "number" ? component : STEP_COMPONENTS.indexOf(component);
    if (bit < 0 || bit >= STEP_COMPONENTS.length) {
      throw new Error(`Unknown step component: ${component}`);
    }

    const stepIndex = clampInt(step, 1, pattern.steps) - 1;
    const rowOffset = patternOffset(pattern, OFF_STEPCOMP) + stepIndex * 16;
    const mask = readU16(this.image, rowOffset) & ~(1 << bit);
    writeU16(this.image, rowOffset, mask);
    this.image[rowOffset + 2 + bit] = 0;

    this.markEdited(trackIndex, patternIndex);
    this.refresh();
  }

  setPatternNotes(trackIndex, patternIndex, notes) {
    const pattern = this.getPattern(trackIndex, patternIndex);
    if (!pattern) return;
    if (notes.length > 120) {
      throw new Error("OP-XY pattern note limit is 120 notes.");
    }

    const sorted = [...notes].sort(noteSort);
    const noteCountOffset = patternOffset(pattern, OFF_NOTE_COUNT);
    const oldCount = this.image[noteCountOffset] || 0;
    const oldStart = noteCountOffset + 1;
    const oldEnd = oldStart + oldCount * NOTE_SIZE;
    const records = new Uint8Array(sorted.length * NOTE_SIZE);

    sorted.forEach((note, index) => {
      const offset = index * NOTE_SIZE;
      const tick = note.tick ?? ((note.step - 1) * STEP_TICKS + (note.tickOffset || 0));
      writeU32(records, offset, Math.max(0, tick));
      writeU32(records, offset + 4, Math.max(1, note.gate || 240));
      records[offset + 8] = Math.max(0, Math.min(127, note.note));
      records[offset + 9] = Math.max(0, Math.min(127, note.velocity ?? 100));
      records[offset + 10] = note.flags?.[0] ?? 0;
      records[offset + 11] = note.flags?.[1] ?? 0;
    });

    const next = new Uint8Array(this.image.length - (oldEnd - oldStart) + records.length);
    next.set(this.image.slice(0, noteCountOffset), 0);
    next[noteCountOffset] = sorted.length;
    next.set(records, noteCountOffset + 1);
    next.set(this.image.slice(oldEnd), noteCountOffset + 1 + records.length);
    this.image = next;
    this.markEdited(trackIndex, patternIndex);
    this.refresh();
  }

  addNote(trackIndex, patternIndex, note) {
    const pattern = this.getPattern(trackIndex, patternIndex);
    this.setPatternNotes(trackIndex, patternIndex, [...pattern.notes, note]);
  }

  updateNote(trackIndex, patternIndex, noteIndex, patch) {
    const pattern = this.getPattern(trackIndex, patternIndex);
    const notes = pattern.notes.map((note) => ({ ...note }));
    if ("step" in patch || "tickOffset" in patch) {
      const nextStep = patch.step ?? notes[noteIndex].step;
      const nextOffset = patch.tickOffset ?? notes[noteIndex].tickOffset ?? 0;
      patch.tick = (nextStep - 1) * STEP_TICKS + nextOffset;
    }
    notes[noteIndex] = { ...notes[noteIndex], ...patch };
    this.setPatternNotes(trackIndex, patternIndex, notes);
  }

  deleteNote(trackIndex, patternIndex, noteIndex) {
    const pattern = this.getPattern(trackIndex, patternIndex);
    this.setPatternNotes(
      trackIndex,
      patternIndex,
      pattern.notes.filter((note) => note.index !== noteIndex),
    );
  }

  markEdited(trackIndex, patternIndex) {
    const pattern = this.getPattern(trackIndex, patternIndex);
    if (!pattern) return;
    const pristineOffset = patternOffset(pattern, OFF_PRISTINE);
    this.image[pristineOffset] = 0;
    this.image[pristineOffset + 1] = 0;
  }

  summarizePattern(pattern) {
    const componentRows = pattern.stepComponents.filter((row) => row.components.length);
    const plockRows = pattern.plocks.filter((row) => row.values.length);
    const nudgedNotes = pattern.notes.filter((note) => note.tickOffset).length;
    const longGates = pattern.notes.filter((note) => note.gate > STEP_TICKS).length;
    const zeroVelocityNotes = pattern.notes.filter((note) => note.velocity === 0).length;
    const silenceComponents = componentRows.reduce((sum, row) => {
      return sum + row.components.filter((component) => {
        return component.name === "velocity" && component.value === 9;
      }).length;
    }, 0);
    const zeroVolumeLocks = plockRows.reduce((sum, row) => {
      return sum + row.values.filter((value) => {
        return (value.name === "volume" || value.name === "engine volume") && value.value === 0;
      }).length;
    }, 0);

    return {
      notes: pattern.noteCount,
      componentSteps: componentRows.length,
      componentCount: componentRows.reduce((sum, row) => sum + row.components.length, 0),
      plockSteps: plockRows.length,
      plockCount: plockRows.reduce((sum, row) => sum + row.values.length, 0),
      nudgedNotes,
      longGates,
      zeroVelocityNotes,
      silenceComponents,
      zeroVolumeLocks,
      hiddenBehaviorCount: componentRows.length + plockRows.length + nudgedNotes + longGates,
    };
  }

  summarizeTrack(track) {
    const patternSummaries = track.patterns.map((pattern) => this.summarizePattern(pattern));
    return patternSummaries.reduce((summary, current) => {
      for (const [key, value] of Object.entries(current)) {
        summary[key] = (summary[key] || 0) + value;
      }
      return summary;
    }, {
      patterns: track.patternCount,
      notes: 0,
      componentSteps: 0,
      componentCount: 0,
      plockSteps: 0,
      plockCount: 0,
      nudgedNotes: 0,
      longGates: 0,
      zeroVelocityNotes: 0,
      silenceComponents: 0,
      zeroVolumeLocks: 0,
      hiddenBehaviorCount: 0,
    });
  }

  getProjectSummary() {
    const trackSummaries = this.tracks.map((track) => this.summarizeTrack(track));
    const totals = trackSummaries.reduce((summary, current) => {
      for (const [key, value] of Object.entries(current)) {
        summary[key] = (summary[key] || 0) + value;
      }
      return summary;
    }, {});
    const sceneSequencerMutes = this.scenes.reduce((sum, scene) => {
      return sum + scene.mutes.filter(Boolean).length;
    }, 0);
    const arrangedSongs = this.songs.filter((song) => {
      return song.sceneCount > 1 || song.sceneIds[0] !== 0 || !song.loop;
    }).length;

    return {
      name: this.name,
      tempo: this.tempo,
      decodedBytes: this.image.length,
      tracks: this.tracks.length,
      scenes: this.scenes.length,
      songs: arrangedSongs || (this.songs.length ? 1 : 0),
      songSlots: this.songs.length,
      arrangedSongs,
      patterns: this.tracks.reduce((sum, track) => sum + track.patternCount, 0),
      sceneSequencerMutes,
      ...totals,
    };
  }

  getPatternUsage(trackIndex, patternIndex) {
    return this.scenes
      .filter((scene) => scene.selectedPatterns[trackIndex] === patternIndex)
      .map((scene) => ({
        sceneIndex: scene.index,
        label: scene.label,
        muted: Boolean(scene.mutes[trackIndex]),
      }));
  }

  getSceneCellSummary(sceneIndex, trackIndex) {
    const scene = this.scenes[sceneIndex];
    const track = this.tracks[trackIndex];
    if (!scene || !track) return null;
    const patternIndex = Math.min(scene.selectedPatterns[trackIndex] || 0, track.patternCount - 1);
    const pattern = track.patterns[patternIndex];
    const summary = this.summarizePattern(pattern);
    const usage = this.getPatternUsage(trackIndex, patternIndex);
    return {
      scene,
      track,
      pattern,
      patternIndex,
      summary,
      usage,
      sequencerMuted: Boolean(scene.mutes[trackIndex]),
      reused: usage.length > 1,
    };
  }

  getTrackPatternOptions(trackIndex) {
    const track = this.tracks[trackIndex];
    if (!track) return [];
    return track.patterns.map((pattern) => ({
      track,
      pattern,
      summary: this.summarizePattern(pattern),
      usage: this.getPatternUsage(trackIndex, pattern.patternIndex),
    }));
  }

  getFindings() {
    const findings = [];
    for (const track of this.tracks) {
      for (const pattern of track.patterns) {
        const summary = this.summarizePattern(pattern);
        const usage = this.getPatternUsage(track.index, pattern.patternIndex);
        if (usage.length > 1) {
          findings.push({
            severity: "info",
            title: `${track.displayLabel} P${pattern.patternIndex + 1} is reused`,
            detail: `Used by ${usage.length} scenes. Duplicate before editing if you want scene-specific changes.`,
            trackIndex: track.index,
            patternIndex: pattern.patternIndex,
          });
        }
        if (summary.plockCount) {
          findings.push({
            severity: "surface",
            title: `${track.displayLabel} P${pattern.patternIndex + 1} has ${summary.plockCount} p-locks`,
            detail: `${summary.plockSteps} steps contain parameter lock data.`,
            trackIndex: track.index,
            patternIndex: pattern.patternIndex,
          });
        }
        if (summary.componentCount) {
          findings.push({
            severity: "surface",
            title: `${track.displayLabel} P${pattern.patternIndex + 1} has ${summary.componentCount} step components`,
            detail: `${summary.componentSteps} steps contain sequencer logic.`,
            trackIndex: track.index,
            patternIndex: pattern.patternIndex,
          });
        }
        if (summary.silenceComponents || summary.zeroVolumeLocks || summary.zeroVelocityNotes) {
          findings.push({
            severity: "warn",
            title: `${track.displayLabel} P${pattern.patternIndex + 1} may contain silent steps`,
            detail: `${summary.silenceComponents} velocity-mute components, ${summary.zeroVolumeLocks} zero-volume locks, ${summary.zeroVelocityNotes} zero-velocity notes.`,
            trackIndex: track.index,
            patternIndex: pattern.patternIndex,
          });
        }
      }
    }

    for (const scene of this.scenes) {
      const muteCount = scene.mutes.filter(Boolean).length;
      if (muteCount) {
        findings.push({
          severity: "info",
          title: `${scene.label} has ${muteCount} sequencer mutes`,
          detail: "Scene mutes stop sequenced notes and MIDI output, but are scene-scoped.",
          sceneIndex: scene.index,
        });
      }
    }

    return findings;
  }
}
